/**
 * Modal dialog on the native <dialog> element: showModal() gives a real focus
 * trap, makes the page behind inert and handles Escape. Rendered only while
 * open. Focus returns to whatever was focused before it opened.
 */
import { useLayoutEffect, useRef, type MouseEvent, type ReactNode, type SyntheticEvent } from 'react';
import './ui.css';

export interface DialogProps {
  onClose(): void;
  /** id of the element that names the dialog (usually its heading)… */
  labelledBy?: string;
  /** …or a name, when the content has no heading of its own we can point at. */
  label?: string;
  describedBy?: string;
  /** center: a card in the middle; sheet: slides up from the bottom on phones. */
  variant?: 'center' | 'sheet';
  /** False = Escape and backdrop taps do nothing (the dialog has its own buttons). */
  dismissible?: boolean;
  className?: string;
  children: ReactNode;
}

export function Dialog({
  onClose,
  labelledBy,
  label,
  describedBy,
  variant = 'center',
  dismissible = true,
  className,
  children,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialog.open) dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  const onCancel = (e: SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault(); // we close through React state, not natively
    if (dismissible) onClose();
  };

  // A click whose target is the <dialog> itself landed on the backdrop (content sits in the inner box).
  const onClick = (e: MouseEvent<HTMLDialogElement>) => {
    if (dismissible && e.target === e.currentTarget) onClose();
  };

  return (
    <dialog
      ref={ref}
      className={['ui-dialog', `ui-dialog--${variant}`, className].filter(Boolean).join(' ')}
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : label}
      aria-describedby={describedBy}
      onCancel={onCancel}
      onClick={onClick}
    >
      <div className="ui-dialog__box">{children}</div>
    </dialog>
  );
}
